// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/profile-input.ts (types now come from contracts/onboarding-session.ts)
/**
 * The questionnaire's answers as typed brand-profile input: ranges and tiers
 * instead of option ids, so the research and the recommendations read numbers.
 *
 * The shape follows what the competitive-intelligence service already accepts:
 * `quizAnswers` on a workflow and the briefing's `userContext`
 * (`challengeKey`, `challengeRaw`, `spendTier`). The tiers use
 * the scales the discovery match compares brands on (`lib/discovery-match.ts`),
 * so a stated spend can place the brand among same-league competitors.
 *
 * The session posts it once at quiz-done (`POST /sessions/:id/quiz`). A skipped question or "Prefer not to say" leaves the
 * field out: an absent value, never a guessed default.
 */
import {
  MAX_CHANNELS,
  MAX_PAINS,
  MAX_SHORT_TEXT_LENGTH,
  type AdSpendTier,
  type BrandProfileInput,
  type ChallengeKey,
  type ChannelShare,
  type GrowthStage,
  type UsdRange,
} from '@/contracts/onboarding-session'

import { pruneOrphans } from './prune'
import { paidChannels, type Answers } from './questions'

export type { AdSpendTier, BrandProfileInput, ChallengeKey, ChannelShare, GrowthStage, UsdRange };

const REVENUE: Record<string, { range: UsdRange; stage: GrowthStage }> = {
  lt250k: { range: { min: 0, max: 250_000 }, stage: 'startup' },
  '250k-1m': { range: { min: 250_000, max: 1_000_000 }, stage: 'early_growth' },
  '1-5m': { range: { min: 1_000_000, max: 5_000_000 }, stage: 'scaling' },
  '5-15m': { range: { min: 5_000_000, max: 15_000_000 }, stage: 'established' },
  '15-50m': { range: { min: 15_000_000, max: 50_000_000 }, stage: 'established' },
  '50m+': { range: { min: 50_000_000 }, stage: 'mature' },
}

const SPEND: Record<string, { range: UsdRange; tier: AdSpendTier }> = {
  lt10k: { range: { min: 0, max: 10_000 }, tier: 'minimal' },
  '10-50k': { range: { min: 10_000, max: 50_000 }, tier: 'light' },
  '50-250k': { range: { min: 50_000, max: 250_000 }, tier: 'moderate' },
  '250k+': { range: { min: 250_000 }, tier: 'heavy' },
}

/** The main channel's share of spend for each `mainShare` answer. */
const MAIN_SHARE: Record<string, { min: number; max: number }> = {
  most: { min: 0.8, max: 1 },
  majority: { min: 0.5, max: 0.8 },
  minority: { min: 0, max: 0.5 },
}

const AOV: Record<string, UsdRange> = {
  lt50: { min: 0, max: 50 },
  '50-100': { min: 50, max: 100 },
  '100-200': { min: 100, max: 200 },
  '200-500': { min: 200, max: 500 },
  '500+': { min: 500 },
}

const CHALLENGE: Record<string, ChallengeKey> = {
  costs: 'cac_rising',
  scale: 'cant_scale',
  fatigue: 'no_creative_direction',
  volume: 'no_creative_direction',
  competitors: 'losing_share',
}

const PAIN_LABEL: Record<string, string> = {
  costs: 'Costs keep rising',
  scale: "Can't scale without losing efficiency",
  fatigue: 'Ads fatigue fast',
  volume: "Can't produce enough creative",
  competitors: 'Competitors are taking share',
  conversion: "Site / landing pages don't convert",
  tracking: "Can't trust our tracking",
  starting: 'Just starting',
}

/** Own-property lookup: a typed "constructor" or "__proto__" must not read off Object.prototype. */
function lookup<T>(table: Record<string, T>, key: string | undefined): T | undefined {
  return key !== undefined && Object.hasOwn(table, key) ? table[key] : undefined
}

/**
 * Cuts free text to the wire limit (UTF-16 units, as the schema counts them) without leaving half a
 * surrogate pair. The inputs carry `maxLength` already; this keeps a pasted or stored value from
 * failing the whole POST.
 */
function clamp(text: string, max: number = MAX_SHORT_TEXT_LENGTH): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  const last = cut.charCodeAt(cut.length - 1)
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut
}

/** The one picked option of an answered single-choice question. */
const single = (answers: Answers, id: string) => {
  const a = answers[id]
  return a && !a.skipped ? a.selected[0] : undefined
}

const SUBSCRIPTION_MODELS = ['replenishment', 'box', 'service'] as const satisfies readonly NonNullable<BrandProfileInput['subscriptionModel']>[]

/** Shares are fractions; two decimals keep 1 - 0.8 from reading 0.19999… */
const round2 = (n: number) => Math.round(n * 100) / 100

/** Ranked channels with their share of spend, or undefined when none were named (skipped, or no paid ads). */
function channelMix(answers: Answers): ChannelShare[] | undefined {
  const channels = paidChannels(answers)
    .map((c) => clamp(c))
    .filter((c) => c.length > 0)
    .slice(0, MAX_CHANNELS)
  if (channels.length === 0) return undefined
  const main = single(answers, 'mainShare')
  return channels.map((channel, i) => {
    const rank = i + 1
    if (channels.length === 1) return { channel, rank, share: { min: 1, max: 1 } }
    const top = lookup(MAIN_SHARE, main)
    if (!top) return { channel, rank }
    if (i === 0) return { channel, rank, share: top }
    // The rest share what's left; one channel alone gets all of it.
    const rest = { min: round2(1 - top.max), max: round2(1 - top.min) }
    return channels.length === 2 ? { channel, rank, share: rest } : { channel, rank, share: { min: 0, max: rest.max } }
  })
}

/** Drops keys whose value is undefined, so "absent" is literal (`'x' in input` is false), not just `=== undefined`. */
function withoutUndefined<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T
}

export function toBrandProfileInput(raw: Answers): BrandProfileInput {
  // Ours: answers of questions that have left the plan (Back and a changed answer) must not reach the profile.
  const answers = pruneOrphans(raw)
  const revenue = lookup(REVENUE, single(answers, 'revenue'))
  const spendId = single(answers, 'spend')
  const spend = lookup(SPEND, spendId)
  // True when a paid channel was named; false only when the person chose "none"; absent when the
  // channels question was skipped or not answered (we do not know).
  const runsPaidAds = paidChannels(answers).length > 0 ? true : single(answers, 'channels') === 'none' ? false : undefined

  const painsAnswer = answers.pains && !answers.pains.skipped ? answers.pains : undefined
  // A custom pain is stored as its own label; "Something else…" typed text arrives as `text`.
  const allPains = painsAnswer ? [...painsAnswer.selected, ...(painsAnswer.text ? [painsAnswer.text] : [])] : []
  const pains = allPains.map((p) => clamp(p)).filter((p) => p.length > 0).slice(0, MAX_PAINS)
  const topPain = pains[0]

  const upsell = single(answers, 'upsell')
  const subscription = single(answers, 'subscription')

  return withoutUndefined({
    annualRevenueUsd: revenue?.range,
    growthStage: revenue?.stage,
    runsPaidAds,
    monthlyAdSpendUsd: spend?.range,
    spendTier: spend && spendId ? clamp(spendId) : undefined,
    adSpendTier: spend?.tier,
    channelMix: channelMix(answers),
    team: clamp(single(answers, 'team') ?? '') || undefined,
    challengeKey: topPain ? (lookup(CHALLENGE, topPain) ?? 'other') : undefined,
    challengeRaw: topPain ? clamp(lookup(PAIN_LABEL, topPain) ?? topPain) : undefined,
    pains: pains.length > 0 ? pains : undefined,
    averageOrderValueUsd: lookup(AOV, single(answers, 'aov')),
    // "Not sure" stays out, like a skip.
    hasPostPurchaseUpsell: upsell === 'yes' ? true : upsell === 'no' ? false : undefined,
    sellsSubscriptions: subscription ? subscription === 'yes' : undefined,
    oneTimePurchase: subscription ? subscription === 'oneTime' : undefined,
    subscriptionModel: SUBSCRIPTION_MODELS.find((m) => m === single(answers, 'subscriptionType')),
  })
}
